import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

import Portfolio from './pages/Portfolio/Portfolio';
import ActivityReports from './pages/ActivityReports/ActivityReports';
import Research from './pages/Research/Research';
import RiskAnalysis from './pages/RiskAnalysis/RiskAnalysis'

import Library from './components/library/library';
import { ThemeProvider } from '@mui/material/styles';
import { vvpTheme } from './vvpTheme';

import {
    BrowserRouter,
    Routes,
    Route,
    createBrowserRouter,
    RouterProvider,
    Navigate
} from "react-router-dom";

import { Provider } from 'react-redux';
import { store } from './store';
import CompanyDetails from './pages/company/CompanyDetails';
import Overview from './pages/company/tabs/overview/Overview';
import FinanceStatements from './pages/company/tabs/finance-statements/FinanceStatements';
import FinancingRounds from './pages/company/tabs/financing-rounds/FinancingRounds';
import CompanyUpdates from './pages/company/tabs/company-updates/CompanyUpdates';
import Documents from './pages/company/tabs/documents/Documents';
import EditCompany from './pages/company/edit/EditCompany';
import CompanyDetailsStep from './pages/company/edit/steps/CompanyDetailsStep';
import OwnershipStep from './pages/company/edit/steps/OwnershipStep';
import InvestmentStep from './pages/company/edit/steps/InvestmentStep';
import GradeReports from './pages/GradeReports/GradeReports';
import SharedRequests from './pages/SharedRequests/SharedRequests';
import RequestDetails from './pages/GradeReports/RequestSettings/RequestDetails';
import RequestSettings from './pages/GradeReports/RequestSettings/tabs/settings/RequestSettings';
import CompaniesSettings from './pages/GradeReports/RequestSettings/tabs/companies/companiesSettings';
import ReviewReport from './pages/GradeReports/ReviewReport/ReviewReport';
import ReportSettings from './pages/GradeReports/RequestSettings/tabs/report/reportSettings';
import CreateRequest from "./pages/CreateActivityRequest/CreateRequest";
import Step1Settings from "./pages/CreateActivityRequest/Step1Settings";
import Step2Companies from './pages/CreateActivityRequest/Step2Companies';
import Step3Report from './pages/CreateActivityRequest/Step3Report';
import { Security, LoginCallback } from '@okta/okta-react';
import { AuthProvider } from './components/auth/AuthProvider';
import { restoreOriginalUri, onAuthRequired, oktaAuth } from './okta/OktaAuth';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { HistoryProvider } from './context/HistoryContext';

const container = document.getElementById('root');
const root = ReactDOM.createRoot(container);

root.render(
    <Provider store={store}>
        <ThemeProvider theme={vvpTheme}>
            <React.StrictMode>
                <BrowserRouter>
                    <Security oktaAuth={oktaAuth} onAuthRequired={onAuthRequired} restoreOriginalUri={restoreOriginalUri}>
                        <AuthProvider>
                            <HistoryProvider>
                                <Routes>
                                    <Route path="/authorization-code/callback" element={<LoginCallback />} />
                                    <Route
                                        path="/*"
                                        element={
                                            <ProtectedRoute>
                                                <App />
                                            </ProtectedRoute>
                                        }>
                                        {/* Add default redirect to portfolio */}
                                        <Route index element={<Navigate to="/portfolio" replace />} />
                                        <Route path='portfolio' element={<Portfolio />} />
                                        <Route path='activity-reports' element={<ActivityReports />} />
                                        <Route path='research' element={<Research />} />
                                        <Route path='risk-analysis' element={<RiskAnalysis />} />
                                        <Route path='library' element={<Library />} />
                                        {/* ✅ Added Company Details (Nested Routes) */}
                                        <Route path="company/:companyId" element={<CompanyDetails />}>
                                            <Route index element={<Overview />} />
                                            <Route path="overview" element={<Overview />} />
                                            <Route path="finance-statements" element={<FinanceStatements />} />
                                            <Route path="financing-rounds" element={<FinancingRounds />} />
                                            <Route path="company-updates" element={<CompanyUpdates />} />
                                            <Route path="documents" element={<Documents />} />
                                        </Route>
                                        {/* ✅ Added Edit Flow Stepper */}
                                        <Route path="company/:companyId/edit" element={<EditCompany />}>
                                            <Route index element={<CompanyDetailsStep />} />
                                            <Route path="details" element={<CompanyDetailsStep />} />
                                            <Route path="ownership" element={<OwnershipStep />} />
                                            <Route path="investment" element={<InvestmentStep />} />
                                        </Route>
                                        <Route path='grade-reports' element={<GradeReports />} />
                                        {/* Additional routes can be added here */}
                                        <Route index element={<GradeReports />} />
                                        <Route path="grade-reports/:companyId/*" element={<> <GradeReports allowedRoles={['operations']} /> </>}>
                                        </Route>
                                        <Route path="grade-reports/:companyId/request-settings" element={<RequestDetails />}>
                                            <Route index element={<RequestSettings />} />
                                            <Route path="settings" element={<RequestSettings />} />
                                            <Route path="companies" element={<CompaniesSettings />} />
                                            <Route path="report" element={<ReportSettings />} />
                                        </Route>

                                        <Route path="grade-reports/:companyId/review-report" element={<ReviewReport />} />

                                        {/* Added Create Request Flow Stepper */}
                                        <Route path="create-request" element={<CreateRequest />}>
                                            {/* Base paths for NEW requests (no ID yet) */}
                                            <Route index element={<Step1Settings />} />
                                            <Route path="settings" element={<Step1Settings />} />
                                            <Route path="companies" element={<Step2Companies />} />
                                            <Route path="report" element={<Step3Report />} />

                                            {/* Catch undefined explicitly — treat same as new request base paths */}
                                            <Route path="undefined">
                                                <Route path="settings" element={<Step1Settings />} />
                                                <Route path="companies" element={<Step2Companies />} />
                                                <Route path="report" element={<Step3Report />} />
                                            </Route>

                                            {/* Dynamic paths for EDIT (Request Settings) flow */}
                                            {/* URL will look like: /create-request/uuid-123/settings */}
                                            <Route path=":reportId">
                                                <Route path="settings" element={<Step1Settings />} />
                                                <Route path="companies" element={<Step2Companies />} />
                                                <Route path="report" element={<Step3Report />} />
                                            </Route>
                                        </Route>

                                        {/* Shared Requests Route */}
                                        {/* <Route path="shared-requests/:requestId" element={<SharedRequests />} /> */}
                                    </Route>
                                </Routes>
                            </HistoryProvider>
                        </AuthProvider>
                    </Security>
                </BrowserRouter>
            </React.StrictMode>
        </ThemeProvider>
    </Provider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
